import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const StoreContext = createContext(null);
const CART_KEY = "mwz_cart_v1";

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
};

export function StoreProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState(loadCart);
  const [zoneId, setZoneId] = useState(() => localStorage.getItem("mwz_zone") || "");
  const [discountCode, setDiscountCode] = useState(() => localStorage.getItem("mwz_code") || "");

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() =>
      api.post("/auth/refresh").then((r) => setUser(r.data)).catch(() => setUser(false)));
  }, []);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("mwz_zone", zoneId); }, [zoneId]);
  useEffect(() => { localStorage.setItem("mwz_code", discountCode); }, [discountCode]);

  const addItem = useCallback((item) => {
    setCart((c) => {
      const i = c.findIndex((x) => x.product_id === item.product_id && x.size === item.size && (x.colour || "") === (item.colour || ""));
      if (i === -1) return [...c, { ...item, qty: Math.min(item.qty, item.max) }];
      const next = [...c];
      next[i] = { ...next[i], qty: Math.min(next[i].qty + item.qty, item.max), max: item.max };
      return next;
    });
  }, []);
  const same = (x, pid, size, colour) => x.product_id === pid && x.size === size && (x.colour || "") === (colour || "");
  const updateQty = useCallback((pid, size, qty, colour) =>
    setCart((c) => c.map((x) => (same(x, pid, size, colour) ? { ...x, qty: Math.max(1, Math.min(qty, x.max || 20)) } : x))), []);
  const removeItem = useCallback((pid, size, colour) => setCart((c) => c.filter((x) => !same(x, pid, size, colour))), []);
  const clearCart = useCallback(() => { setCart([]); setDiscountCode(""); }, []);

  const value = useMemo(() => ({
    settings, user, setUser, cart, addItem, updateQty, removeItem, clearCart,
    zoneId, setZoneId, discountCode, setDiscountCode,
    cartCount: cart.reduce((s, x) => s + x.qty, 0),
  }), [settings, user, cart, addItem, updateQty, removeItem, clearCart, zoneId, discountCode]);

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export const useStore = () => useContext(StoreContext);
